import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, authReady } from '../services/supabase';

const HEARTBEAT_INTERVAL = 1500; // 1.5s — how often the loop ticks
const LIVENESS_BOUND = 4000; // 4s — how stale last_seen can be before showing grey

/**
 * useHeartbeat — DB-based presence for connection pages.
 * Each user upserts a heartbeat row on an interval. A single RPC call
 * writes our timestamp and returns the peer's last_seen in one round trip.
 *
 * @param {string} chatRoomId
 * @param {string} senderId
 * @returns {boolean} peerOnline — true if peer's heartbeat is fresh
 */
const useHeartbeat = (chatRoomId, senderId) => {
  const [peerOnline, setPeerOnline] = useState(false);
  const intervalRef = useRef(null);
  // Initialize to 0 so dot starts grey (Date.now() - 0 > LIVENESS_BOUND)
  const lastConfirmedRef = useRef(0);
  const prevServerLastSeenRef = useRef(0);
  const isFirstTickRef = useRef(true);

  const tick = useCallback(async () => {
    if (!chatRoomId || !senderId) return;

    // A session-less tick would be refused (a guest id is written only by
    // its owner) — wait for sign-in; near-instant once it has resolved.
    await authReady;
    const { data, error } = await supabase.rpc('heartbeat_tick', {
      p_chat_room_id: chatRoomId,
      p_sender_id: senderId,
    });

    if (error) {
      console.error('[HEARTBEAT] tick failed:', error.message);
    }

    const peerLastSeen = data ? new Date(data).getTime() : null;
    if (peerLastSeen && peerLastSeen !== prevServerLastSeenRef.current) {
      if (isFirstTickRef.current) {
        // First tick: store baseline but don't confirm liveness.
        // A stale DB row from weeks ago would otherwise flash green for ~4s.
        prevServerLastSeenRef.current = peerLastSeen;
      } else {
        // Subsequent ticks: timestamp advanced → peer is actively heartbeating
        prevServerLastSeenRef.current = peerLastSeen;
        lastConfirmedRef.current = Date.now();
      }
    }
    isFirstTickRef.current = false;

    const isOnline = Date.now() - lastConfirmedRef.current < LIVENESS_BOUND;
    setPeerOnline((prev) => {
      if (prev !== isOnline) {
        console.log(`[HEARTBEAT] ${isOnline ? 'online' : 'offline'}`);
      }
      return isOnline;
    });
  }, [chatRoomId, senderId]);

  useEffect(() => {
    if (!chatRoomId || !senderId) return;

    // Reset refs on mount / dependency change
    lastConfirmedRef.current = 0;
    prevServerLastSeenRef.current = 0;
    isFirstTickRef.current = true;
    setPeerOnline(false);

    // Fire immediately, then start interval
    tick();
    intervalRef.current = setInterval(tick, HEARTBEAT_INTERVAL);

    // Visibility handlers — stop heartbeat when backgrounded, resume on return
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      } else {
        // Visible again — tick immediately, restart interval
        tick();
        if (!intervalRef.current) {
          intervalRef.current = setInterval(tick, HEARTBEAT_INTERVAL);
        }
      }
    };

    // pagehide as belt-and-suspenders for Safari iOS standalone/PWA
    const handlePageHide = () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [chatRoomId, senderId, tick]);

  return peerOnline;
};

export default useHeartbeat;
